import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';
import { WorkflowKpiCardGrid } from '@/components/workflow/WorkflowKpiCardGrid';
import { DwellAlertsCard } from '@/components/workflow/DwellAlertsCard';
import { SlaComplianceCard } from '@/components/workflow/SlaComplianceCard';
import { useWorkflowBindings } from '@/api/hooks/useWorkflows';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import type { ChartConfig } from '@/components/ui/chart';
import {
  useDashboardStats,
  useRecentIncidents,
  useInventoryStats,
  useTrends,
  useRecentRequisitions,
  useAssetsByStatus,
  useAssetsByClass,
  useAssetsTimeline,
  useIncidentTrends,
  useDrillDown,
} from '@/api/hooks/useDashboard';
import type { DrillDownKpi } from '@/api/hooks/useDashboard';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area, ResponsiveContainer,
} from 'recharts';
import {
  Package,
  AlertTriangle,
  Radio,
  ClipboardList,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle,
  ImageIcon,
  FileText,
  XCircle,
  PlayCircle,
  LayoutDashboard,
  CalendarRange,
  ArrowRight,
  Search,
  Loader2,
  Truck,
  PackageX,
  Ban,
  Tag,
  Hourglass,
  Sparkles,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { DeliveryScheduler, InventoryScheduler } from '@/components/dashboard';
import { MonthlyOperations } from '@/components/dashboard/MonthlyOperations';
import { useAiAssistantStore } from '@/stores/aiAssistantStore';
import { HealthBriefingCard } from '@/components/ai/HealthBriefingCard';

// KPI Card Component
function KpiCard({
  title,
  value,
  description,
  icon: Icon,
  trend,
  isLoading,
  onClick,
  detailLabel,
}: {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ElementType;
  trend?: { value: number; isPositive: boolean };
  isLoading?: boolean;
  onClick?: () => void;
  detailLabel?: string;
}) {
  return (
    <Card
      className={onClick ? 'cursor-pointer transition-colors hover:bg-accent/50' : undefined}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
        {trend && !isLoading && (
          <div className={`flex items-center text-xs ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className={`mr-1 h-3 w-3 ${!trend.isPositive && 'rotate-180'}`} />
            {trend.value}% from last month
          </div>
        )}
      </CardContent>
      {onClick && detailLabel && (
        <CardFooter className="border-t px-6 py-2">
          <span className="text-xs text-primary flex items-center gap-1">
            {detailLabel} <ArrowRight className="h-3 w-3" />
          </span>
        </CardFooter>
      )}
    </Card>
  );
}

// Priority badge colors
function getPriorityColor(priority: string): string {
  switch (priority.toLowerCase()) {
    case 'critical':
      return 'bg-red-500 hover:bg-red-600';
    case 'high':
      return 'bg-orange-500 hover:bg-orange-600';
    case 'medium':
      return 'bg-yellow-500 hover:bg-yellow-600';
    case 'low':
      return 'bg-blue-500 hover:bg-blue-600';
    default:
      return 'bg-gray-500 hover:bg-gray-600';
  }
}

// Bar chart colors palette
const BAR_COLORS = [
  'hsl(217, 91%, 60%)',  // blue
  'hsl(142, 71%, 45%)',  // green
  'hsl(45, 93%, 47%)',   // amber
  'hsl(262, 83%, 58%)',  // purple
  'hsl(0, 84%, 60%)',    // red
  'hsl(190, 90%, 50%)',  // cyan
  'hsl(25, 95%, 53%)',   // orange
  'hsl(330, 81%, 60%)',  // pink
];

export default function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const openAiWith = useAiAssistantStore((s) => s.openWith);
  const features = useFeatureFlags();
  // Pallet-incidents module: the losses tile is meaningless without it.
  const palletIncidentsEnabled = !!features.palletIncidents;
  // Pallet tenants (TOCA): the monthly-operations section replaces the generic
  // asset charts — by status, by class, incident trend and both inventory
  // statistics widgets — so those are neither rendered nor fetched.
  const monthlyOps = !!features.monthlyOperations;

  // Fetch dashboard data from API
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: recentIncidents, isLoading: incidentsLoading } = useRecentIncidents(5);
  const { data: inventoryStats, isLoading: inventoryStatsLoading } = useInventoryStats(!monthlyOps);
  const { data: trends, isLoading: trendsLoading } = useTrends(6);
  const { data: recentRequisitions, isLoading: requisitionsLoading } = useRecentRequisitions(5);

  // New chart data
  const { data: assetsByStatus, isLoading: statusLoading } = useAssetsByStatus(!monthlyOps);
  const { data: assetsByClass, isLoading: classLoading } = useAssetsByClass(!monthlyOps);
  const { data: assetsTimeline, isLoading: timelineLoading } = useAssetsTimeline(6);
  const { data: incidentTrends, isLoading: incidentTrendsLoading } = useIncidentTrends(6, !monthlyOps);

  // Drill-down state
  const [drillDown, setDrillDown] = useState<DrillDownKpi | null>(null);
  const [drillDownSearch, setDrillDownSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(drillDownSearch), 350);
    return () => clearTimeout(timer);
  }, [drillDownSearch]);

  const {
    data: drillDownPages,
    isLoading: drillDownLoading,
    isError: drillDownError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDrillDown(drillDown, debouncedSearch);

  const drillDownRecords = useMemo(() => {
    if (!drillDownPages?.pages.length) return { type: null as string | null, data: [] as Record<string, any>[], totalCount: 0 };
    const first = drillDownPages.pages[0];
    return {
      type: first.type,
      data: drillDownPages.pages.flatMap(p => p.data as Record<string, any>[]),
      totalCount: first.totalCount,
    };
  }, [drillDownPages]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: scrollRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const drillDownKpiTitle: Record<DrillDownKpi, string> = {
    assetsWithoutEpc: t('stats.assetsWithoutEpc'),
    availablePallets: t('stats.availableAssets'),
    damagedPallets: t('stats.damagedAssets'),
    assignedPallets: t('stats.assignedAssets'),
    recoveredPallets: t('stats.recoveredAssets'),
    staleDelivered: t('stats.staleDelivered', { days: stats?.staleDeliveredDays ?? 90 }),
  };

  // Bajas as a share of every pallet that has been in service: the active ones
  // plus the ones already given de baja. A count alone cannot say whether it is
  // getting better or worse as the fleet grows.
  const navigate = useNavigate();
  const fleetShare = (count: number | undefined) => {
    const fleet = (stats?.totalAssets ?? 0) + (stats?.destroyedPallets ?? 0) + (stats?.damagedEpcs ?? 0);
    return fleet > 0 ? (((count ?? 0) / fleet) * 100).toFixed(1) : '0.0';
  };

  // Chart configs
  const statusChartConfig = useMemo((): ChartConfig => {
    if (!assetsByStatus) return {};
    const config: ChartConfig = {};
    assetsByStatus.forEach((item) => {
      config[item.statusLabel] = { label: t(`charts.status.${item.status}`, item.statusLabel), color: item.color };
    });
    return config;
  }, [assetsByStatus, t]);

  const incidentChartConfig = useMemo((): ChartConfig => ({
    created: { label: t('charts.incidentsCreated'), color: 'hsl(0, 84%, 60%)' },
    resolved: { label: t('charts.incidentsResolved'), color: 'hsl(142, 71%, 45%)' },
  }), [t]);

  // Total for donut center label
  const statusTotal = useMemo(
    () => assetsByStatus?.reduce((sum, item) => sum + item.count, 0) ?? 0,
    [assetsByStatus]
  );

  // Requisition status badge variant
  const getRequisitionStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'Requested':
        return 'secondary';
      case 'Approved':
        return 'default';
      case 'InProgress':
        return 'default';
      case 'Completed':
        return 'outline';
      case 'Rejected':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  // Requisition status icon
  const getRequisitionStatusIcon = (status: string) => {
    switch (status) {
      case 'Requested':
        return <Clock className="h-4 w-4" />;
      case 'Approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'InProgress':
        return <PlayCircle className="h-4 w-4" />;
      case 'Completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'Rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  // Cards that render in different rows depending on the monthly view.
  const recentRequisitionsCard = (
    <Card>
      <CardHeader>
        <CardTitle>{t('widgets.recentRequisitions')}</CardTitle>
        <CardDescription>{t('widgets.recentRequisitionsDesc')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {requisitionsLoading ? (
            <>
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-10 w-10 rounded" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-3 w-[150px]" />
                  </div>
                </div>
              ))}
            </>
          ) : recentRequisitions && recentRequisitions.length > 0 ? (
            recentRequisitions.map((req) => {
              const progress = req.requestedQuantity > 0
                ? Math.round((req.suppliedQuantity / req.requestedQuantity) * 100)
                : 0;
              return (
                <div
                  key={req.id}
                  className="flex items-center justify-between space-x-4 rounded-lg border p-3"
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="flex h-10 w-10 items-center justify-center rounded bg-muted">
                      {getRequisitionStatusIcon(req.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{req.folio}</p>
                        <Badge variant={getRequisitionStatusVariant(req.status)} className="text-xs">
                          {t(`scheduler.status.${req.status}`, req.status)}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {req.clientName}
                      </p>
                      {req.status === 'InProgress' && (
                        <div className="flex items-center gap-2 mt-1">
                          <Progress value={progress} className="h-1.5 flex-1" />
                          <span className="text-xs text-muted-foreground">
                            {req.suppliedQuantity}/{req.requestedQuantity}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{req.requestedQuantity}</p>
                    <p className="text-xs text-muted-foreground">{t('common:terminology.assets')}</p>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('widgets.noRecentRequisitions')}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const trendsCard = (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('charts.trendsTitle')}</CardTitle>
        <CardDescription>{t('charts.trendsDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        {trendsLoading ? (
          <div className="h-[280px] flex items-center justify-center">
            <Skeleton className="h-full w-full" />
          </div>
        ) : trends && trends.length > 0 ? (
          <div className="h-[280px] flex flex-col">
            {(() => {
              const maxValue = Math.max(...trends.map(t => Math.max(t.deliveries, t.recoveries)), 1);
              return (
                <div className="flex flex-1 items-end justify-around gap-1 pb-2">
                  {trends.map((item, index) => (
                    <div key={index} className="flex flex-col items-center gap-1 flex-1 min-w-0">
                      <div className="flex gap-0.5 h-[180px] items-end w-full justify-center">
                        <div
                          className="bg-primary/80 w-4 md:w-5 rounded-t transition-all"
                          style={{
                            height: `${Math.min((item.deliveries / maxValue) * 100, 100)}%`,
                            minHeight: item.deliveries > 0 ? '4px' : '0px',
                          }}
                          title={`${t('charts.deliveries')}: ${item.deliveries.toLocaleString()}`}
                        />
                        <div
                          className="bg-green-500/80 w-4 md:w-5 rounded-t transition-all"
                          style={{
                            height: `${Math.min((item.recoveries / maxValue) * 100, 100)}%`,
                            minHeight: item.recoveries > 0 ? '4px' : '0px',
                          }}
                          title={`${t('charts.recoveries')}: ${item.recoveries.toLocaleString()}`}
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                        {item.month}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="flex justify-center gap-4 pt-2 border-t">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 bg-primary/80 rounded" />
                <span className="text-xs">{t('charts.deliveries')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 bg-green-500/80 rounded" />
                <span className="text-xs">{t('charts.recoveries')}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-[280px] flex items-center justify-center">
            <p className="text-sm text-muted-foreground">{t('widgets.noDataAvailable')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const dashboardContent = (
    <>
      {/* Core KPIs - Always visible */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title={t('stats.totalAssets')}
          value={stats?.totalAssets?.toLocaleString() ?? 0}
          description={t('stats.totalAssetsDesc')}
          icon={Package}
          isLoading={statsLoading}
        />
        <KpiCard
          title={t('stats.criticalIncidents')}
          value={stats?.criticalIncidents ?? 0}
          description={t('stats.criticalIncidentsDesc')}
          icon={AlertTriangle}
          isLoading={statsLoading}
        />
        <KpiCard
          title={t('stats.assetsWithoutEpc')}
          value={stats?.assetsWithoutEpc ?? 0}
          description={t('stats.assetsWithoutEpcDesc')}
          icon={Radio}
          isLoading={statsLoading}
          onClick={() => setDrillDown('assetsWithoutEpc')}
          detailLabel={t('drilldown.viewDetail')}
        />
        <KpiCard
          title={t('stats.openInventories')}
          value={stats?.openInventories ?? 0}
          description={t('stats.openInventoriesDesc')}
          icon={ClipboardList}
          isLoading={statsLoading}
        />
      </div>

      {/* Phase 6 — Workflow-aware KPI tiles. Renders nothing for tenants
          with no bound shipment-aware workflow (TOCA today), nothing for
          SGS (endpoint 404s), and nothing when feature.dynamicKpis is off.
          When a Hospital Pilot binds a workflow with kpi_role-tagged states,
          a row of cards appears here above the legacy TOCA tiles. The
          legacy tiles stay byte-for-byte unchanged. */}
      {features.dynamicKpis && <DynamicWorkflowKpis />}

      {/* Toca-specific KPIs */}
      {features.advancedDashboard && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            title={t('stats.availableAssets')}
            value={stats?.availablePallets ?? 0}
            description={t('stats.availableAssetsDesc')}
            icon={CheckCircle}
            isLoading={statsLoading}
            onClick={() => setDrillDown('availablePallets')}
            detailLabel={t('drilldown.viewDetail')}
          />
          <KpiCard
            title={t('stats.damagedAssets')}
            value={stats?.damagedPallets ?? 0}
            description={t('stats.damagedAssetsDesc')}
            icon={AlertCircle}
            isLoading={statsLoading}
            onClick={() => setDrillDown('damagedPallets')}
            detailLabel={t('drilldown.viewDetail')}
          />
          <KpiCard
            title={t('stats.assignedAssets')}
            value={stats?.assignedPallets ?? 0}
            description={t('stats.assignedAssetsDesc')}
            icon={Package}
            isLoading={statsLoading}
            onClick={() => setDrillDown('assignedPallets')}
            detailLabel={t('drilldown.viewDetail')}
          />
          <KpiCard
            title={t('stats.recoveredAssets')}
            value={stats?.recoveredPallets ?? 0}
            description={t('stats.recoveredAssetsDesc')}
            icon={TrendingUp}
            isLoading={statsLoading}
            onClick={() => setDrillDown('recoveredPallets')}
            detailLabel={t('drilldown.viewDetail')}
          />
        </div>
      )}

      {/* Requisition KPI - Toca only */}
      {features.requisitions && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            title={t('stats.pendingRequisitions')}
            value={stats?.pendingRequisitions ?? 0}
            description={t('stats.pendingRequisitionsDesc')}
            icon={Clock}
            isLoading={statsLoading}
          />
          <KpiCard
            title={t('stats.pendingDeliveries')}
            value={stats?.pendingDeliveries ?? 0}
            description={t('stats.pendingDeliveriesDesc')}
            icon={Package}
            isLoading={statsLoading}
          />
          <KpiCard
            title={t('stats.deliveredPallets')}
            value={(stats?.deliveredPallets ?? 0).toLocaleString()}
            description={t('stats.deliveredPalletsDesc')}
            icon={Truck}
            isLoading={statsLoading}
          />
          {/* Confirmed losses. Gated on the module flag rather than on the
              value, so a tenant with the module on and zero losses still sees
              the tile — "zero lost" is meaningful information. */}
          {palletIncidentsEnabled && (
            <KpiCard
              title={t('stats.lostPallets')}
              value={(stats?.lostPallets ?? 0).toLocaleString()}
              description={t('stats.lostPalletsDesc')}
              icon={PackageX}
              isLoading={statsLoading}
            />
          )}
        </div>
      )}

      {/* Bajas: the end of a pallet's life, counted from the baja folios (a
          retired pallet is inactive, so it already left every tile above), and
          the pallets that age in Entregado, which is where one that came back
          with a dead tag hides. */}
      {features.advancedDashboard && (
        <div className="grid gap-4 md:grid-cols-3">
          <KpiCard
            title={t('stats.destroyedPallets')}
            value={(stats?.destroyedPallets ?? 0).toLocaleString()}
            description={t('stats.retiredDesc', {
              count: stats?.destroyedPalletsRecent ?? 0,
              days: stats?.retiredRecentDays ?? 30,
              share: fleetShare(stats?.destroyedPallets),
            })}
            icon={Ban}
            isLoading={statsLoading}
            onClick={() => navigate('/damage?tab=retirements&reason=2')}
            detailLabel={t('drilldown.viewDetail')}
          />
          <KpiCard
            title={t('stats.damagedEpcs')}
            value={(stats?.damagedEpcs ?? 0).toLocaleString()}
            description={t('stats.retiredDesc', {
              count: stats?.damagedEpcsRecent ?? 0,
              days: stats?.retiredRecentDays ?? 30,
              share: fleetShare(stats?.damagedEpcs),
            })}
            icon={Tag}
            isLoading={statsLoading}
            onClick={() => navigate('/damage?tab=retirements&reason=1')}
            detailLabel={t('drilldown.viewDetail')}
          />
          <KpiCard
            title={t('stats.staleDelivered', { days: stats?.staleDeliveredDays ?? 90 })}
            value={(stats?.staleDeliveredPallets ?? 0).toLocaleString()}
            description={t('stats.staleDeliveredDesc')}
            icon={Hourglass}
            isLoading={statsLoading}
            onClick={() => setDrillDown('staleDelivered')}
            detailLabel={t('drilldown.viewDetail')}
          />
        </div>
      )}

      {/* Monthly operations (Pallet tenants) — replaces the generic asset
          charts below with what a pallet-rental month actually looks like. */}
      {monthlyOps && <MonthlyOperations />}

      {/* Charts Row 1: Donut (status) + Bar (class) */}
      {!monthlyOps && (
      <div className="grid gap-4 md:grid-cols-2">
        {/* Assets by Status — Donut Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('charts.assetsByStatusTitle')}</CardTitle>
            <CardDescription>{t('charts.assetsByStatusDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {statusLoading ? (
              <div className="h-[280px] flex items-center justify-center">
                <Skeleton className="h-[240px] w-[240px] rounded-full" />
              </div>
            ) : assetsByStatus && assetsByStatus.length > 0 ? (
              <ChartContainer config={statusChartConfig} className="h-[280px] w-full">
                <PieChart>
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Pie
                    data={assetsByStatus.map((item) => ({
                      name: t(`charts.status.${item.status}`, item.statusLabel),
                      value: item.count,
                      fill: item.color,
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    strokeWidth={2}
                    stroke="hsl(var(--background))"
                  >
                    {assetsByStatus.map((item, index) => (
                      <Cell key={index} fill={item.color} />
                    ))}
                  </Pie>
                  <text x="50%" y="48%" textAnchor="middle" className="fill-foreground text-2xl font-bold">
                    {statusTotal}
                  </text>
                  <text x="50%" y="56%" textAnchor="middle" className="fill-muted-foreground text-xs">
                    {t('stats.totalAssets')}
                  </text>
                </PieChart>
              </ChartContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center">
                <p className="text-sm text-muted-foreground">{t('widgets.noDataAvailable')}</p>
              </div>
            )}
            {assetsByStatus && assetsByStatus.length > 0 && (
              <div className="flex justify-center gap-4 flex-wrap mt-2">
                {assetsByStatus.map((item, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
                    <span className="text-xs text-muted-foreground">
                      {t(`charts.status.${item.status}`, item.statusLabel)} ({item.count})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assets by Class — Bar Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('charts.assetsByClassTitle')}</CardTitle>
            <CardDescription>{t('charts.assetsByClassDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {classLoading ? (
              <div className="h-[280px] flex items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : assetsByClass && assetsByClass.length > 0 ? (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={assetsByClass}
                    layout="vertical"
                    margin={{ top: 0, right: 16, bottom: 0, left: 0 }}
                  >
                    <CartesianGrid horizontal={false} strokeDasharray="3 3" className="stroke-border/50" />
                    <XAxis type="number" tickLine={false} axisLine={false} className="text-xs fill-muted-foreground" />
                    <YAxis
                      type="category"
                      dataKey="className"
                      tickLine={false}
                      axisLine={false}
                      width={120}
                      className="text-xs fill-muted-foreground"
                      tick={{ fontSize: 11 }}
                    />
                    <ChartTooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const data = payload[0].payload;
                        return (
                          <div className="rounded-lg border bg-background px-3 py-2 text-xs shadow-md">
                            <p className="font-medium">{data.className}</p>
                            <p className="text-muted-foreground">{data.count} {t('charts.assets')}</p>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={24}>
                      {assetsByClass.map((_entry, index) => (
                        <Cell key={index} fill={BAR_COLORS[index % BAR_COLORS.length]} fillOpacity={0.85} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center">
                <p className="text-sm text-muted-foreground">{t('widgets.noDataAvailable')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Charts Row 2: Assets timeline + Incident trends (or, with the monthly
          view, the delivery/recovery trend) */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Asset Registration Timeline — Bar Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('charts.assetsTimelineTitle')}</CardTitle>
            <CardDescription>{t('charts.assetsTimelineDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {timelineLoading ? (
              <div className="h-[300px] flex items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : assetsTimeline && assetsTimeline.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={assetsTimeline}
                    margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                  >
                    <defs>
                      <linearGradient id="gradTimeline" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(217, 91%, 60%)" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="hsl(217, 91%, 60%)" stopOpacity={0.4} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
                    <XAxis
                      dataKey="month"
                      tickLine={false}
                      axisLine={false}
                      className="text-xs fill-muted-foreground"
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      className="text-xs fill-muted-foreground"
                      allowDecimals={false}
                    />
                    <ChartTooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const data = payload[0].payload;
                        return (
                          <div className="rounded-lg border bg-background px-3 py-2 text-xs shadow-md">
                            <p className="font-medium">{data.month}</p>
                            <p className="text-muted-foreground">{data.count} {t('charts.assetsRegistered')}</p>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="count" fill="url(#gradTimeline)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center">
                <p className="text-sm text-muted-foreground">{t('widgets.noDataAvailable')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {monthlyOps ? (features.trends ? trendsCard : null) : (
        /* Incident Trends — Area Chart */
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('charts.incidentTrendsTitle')}</CardTitle>
            <CardDescription>{t('charts.incidentTrendsDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {incidentTrendsLoading ? (
              <div className="h-[300px] flex items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : incidentTrends && incidentTrends.length > 0 ? (
              <ChartContainer config={incidentChartConfig} className="h-[300px] w-full">
                <AreaChart data={incidentTrends} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                  <defs>
                    <linearGradient id="gradCreated" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(0, 84%, 60%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(0, 84%, 60%)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradResolved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={false}
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    className="text-xs fill-muted-foreground"
                    allowDecimals={false}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="created"
                    name={t('charts.incidentsCreated')}
                    stroke="hsl(0, 84%, 60%)"
                    fill="url(#gradCreated)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="resolved"
                    name={t('charts.incidentsResolved')}
                    stroke="hsl(142, 71%, 45%)"
                    fill="url(#gradResolved)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center">
                <p className="text-sm text-muted-foreground">{t('widgets.noDataAvailable')}</p>
              </div>
            )}
            {incidentTrends && incidentTrends.length > 0 && (
              <div className="flex justify-center gap-4 mt-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'hsl(0, 84%, 60%)' }} />
                  <span className="text-xs text-muted-foreground">{t('charts.incidentsCreated')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'hsl(142, 71%, 45%)' }} />
                  <span className="text-xs text-muted-foreground">{t('charts.incidentsResolved')}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* Row 3: Recent Incidents + Inventory Stats (or, with the monthly view,
          Recent Requisitions) */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Recent Incidents */}
        <Card>
          <CardHeader>
            <CardTitle>{t('widgets.recentIncidents')}</CardTitle>
            <CardDescription>{t('widgets.recentIncidentsDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {incidentsLoading ? (
                <>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center space-x-4">
                      <Skeleton className="h-10 w-10 rounded" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-[200px]" />
                        <Skeleton className="h-3 w-[150px]" />
                      </div>
                    </div>
                  ))}
                </>
              ) : recentIncidents && recentIncidents.length > 0 ? (
                recentIncidents.map((incident) => (
                  <div
                    key={incident.id}
                    className="flex items-center justify-between space-x-4 rounded-lg border p-3"
                  >
                    <div className="flex items-center space-x-3">
                      {incident.hasImages ? (
                        <div className="flex h-10 w-10 items-center justify-center rounded bg-muted">
                          <ImageIcon className="h-5 w-5 text-muted-foreground" />
                        </div>
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded bg-muted">
                          <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium">{incident.type}</p>
                        <p className="text-xs text-muted-foreground">
                          {incident.assetCode}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={getPriorityColor(incident.priority)}>
                        {incident.priority}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(incident.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('widgets.noRecentIncidents')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {monthlyOps ? (features.requisitions ? recentRequisitionsCard : null) : (
        /* Inventory Statistics */
        <Card>
          <CardHeader>
            <CardTitle>{t('charts.inventoryStatsTitle')}</CardTitle>
            <CardDescription>{t('charts.inventoryStatsDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {inventoryStatsLoading ? (
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center justify-between">
                      <Skeleton className="h-4 w-[100px]" />
                      <Skeleton className="h-4 w-[60px]" />
                    </div>
                  ))}
                </>
              ) : inventoryStats && inventoryStats.length > 0 ? (
                inventoryStats.map((stat, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between border-b pb-2 last:border-0"
                  >
                    <span className="text-sm">{stat.range}</span>
                    <span className="text-sm font-semibold">{stat.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('widgets.noDataAvailable')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Recent Requisitions - Toca only. With the monthly view on it moves
          up, beside Recent Incidents. */}
      {!monthlyOps && features.requisitions && recentRequisitionsCard}

      {/* Charts Row - Toca trends (deliveries & recoveries). With the monthly
          view on, the trend card moves up beside the registration timeline
          and the inventory-completion chart is dropped. */}
      {!monthlyOps && features.trends && (
        <div className="grid gap-4 md:grid-cols-2">
          {trendsCard}

          {/* Inventory Completion Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('charts.inventoryStatsTitle')}</CardTitle>
              <CardDescription>{t('charts.inventoryStatsDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              {inventoryStatsLoading ? (
                <div className="h-[280px] flex items-center justify-center">
                  <Skeleton className="h-full w-full" />
                </div>
              ) : inventoryStats && inventoryStats.length > 0 ? (
                <div className="h-[280px] flex flex-col">
                  {(() => {
                    const total = inventoryStats.reduce((sum, item) => sum + item.count, 0);
                    const maxCount = Math.max(...inventoryStats.map(s => s.count), 1);
                    const colors = ['bg-green-500', 'bg-blue-500', 'bg-yellow-500', 'bg-red-500'];
                    return (
                      <>
                        <div className="flex flex-1 items-end justify-around gap-2 pb-2">
                          {inventoryStats.map((item, index) => (
                            <div key={index} className="flex flex-col items-center gap-1 flex-1 min-w-0">
                              <span className="text-sm font-medium">{item.count}</span>
                              <div className="w-full h-[150px] flex items-end justify-center">
                                <div
                                  className={`${colors[index]} w-10 md:w-14 rounded-t transition-all opacity-80`}
                                  style={{
                                    height: `${Math.min((item.count / maxCount) * 100, 100)}%`,
                                    minHeight: item.count > 0 ? '8px' : '4px',
                                  }}
                                  title={`${item.range}: ${item.count} (${total > 0 ? Math.round((item.count / total) * 100) : 0}%)`}
                                />
                              </div>
                              <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                                {item.range}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-center gap-3 pt-2 border-t flex-wrap">
                          {inventoryStats.map((item, index) => (
                            <div key={index} className="flex items-center gap-1.5">
                              <div className={`w-2.5 h-2.5 ${colors[index]} rounded opacity-80`} />
                              <span className="text-xs">{item.range}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="h-[280px] flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">{t('widgets.noDataAvailable')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pageTitle')}</h1>
          <p className="text-muted-foreground">
            {t('pageDescription')}
          </p>
        </div>
        {features.ai && (
          <Button
            variant="outline"
            className="shrink-0 gap-2"
            onClick={() =>
              openAiWith(
                'Summarize my key dashboard metrics right now, and flag anything that looks off or needs attention this week. Use your data tools to get the real current numbers.',
              )
            }
          >
            <Sparkles className="h-4 w-4 text-primary" />
            {t('explainWithAi', { defaultValue: 'Explain with AI' })}
          </Button>
        )}
      </div>

      {/* Daily Health Briefing. Gated here as well as hiding itself on error:
          mounting it fires the briefing request, which 404s wherever the AI
          module is off — on every dashboard visit. */}
      {features.ai && <HealthBriefingCard />}

      {features.clients ? (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">
              <LayoutDashboard className="h-4 w-4 mr-2" />
              {t('tabs.overview')}
            </TabsTrigger>
            <TabsTrigger value="delivery-scheduler">
              <CalendarRange className="h-4 w-4 mr-2" />
              {t('tabs.deliveryScheduler')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {dashboardContent}
          </TabsContent>

          <TabsContent value="delivery-scheduler">
            <DeliveryScheduler />
          </TabsContent>
        </Tabs>
      ) : (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">
              <LayoutDashboard className="h-4 w-4 mr-2" />
              {t('tabs.overview')}
            </TabsTrigger>
            <TabsTrigger value="inventory-scheduler">
              <CalendarRange className="h-4 w-4 mr-2" />
              {t('tabs.inventoryScheduler')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {dashboardContent}
          </TabsContent>

          <TabsContent value="inventory-scheduler">
            <InventoryScheduler />
          </TabsContent>
        </Tabs>
      )}

      {/* Drill-Down Dialog */}
      <Dialog open={!!drillDown} onOpenChange={(open) => { if (!open) { setDrillDown(null); setDrillDownSearch(''); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{drillDown ? t('drilldown.title', { kpi: drillDownKpiTitle[drillDown] }) : ''}</DialogTitle>
            <DialogDescription>
              {drillDownRecords.totalCount > 0
                ? t('drilldown.subtitle', { count: drillDownRecords.totalCount })
                : ''}
            </DialogDescription>
          </DialogHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('drilldown.search')}
              value={drillDownSearch}
              onChange={(e) => setDrillDownSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          {/* Table */}
          <div ref={scrollRef} className="flex-1 overflow-auto border rounded-md">
            {drillDownLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : drillDownError ? (
              <div className="flex items-center justify-center py-16 text-sm text-destructive">
                Error al cargar los datos. Intente de nuevo.
              </div>
            ) : drillDownRecords.type === 'asset' ? (
              drillDownRecords.data.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 sticky top-0 z-10">
                    <tr className="border-b">
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.assetId')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.name')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.epc')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.location')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.status')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.createdDate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(drillDownRecords.data as any[]).map((r: any, idx: number) => (
                      <tr key={`${r.assetId}-${idx}`} className="border-b hover:bg-muted/30">
                        <td className="px-3 py-2 font-mono text-xs">{r.assetId}</td>
                        <td className="px-3 py-2">{r.name ?? '-'}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.epc ?? '-'}</td>
                        <td className="px-3 py-2">{r.locationName ?? '-'}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline">{r.statusLabel}</Badge>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {format(new Date(r.createdDate), 'd MMM yyyy', { locale: es })}
                        </td>
                      </tr>
                    ))}
                    <tr><td colSpan={6}><div ref={sentinelRef} className="h-1" /></td></tr>
                  </tbody>
                  {isFetchingNextPage && (
                    <tfoot>
                      <tr>
                        <td colSpan={6} className="text-center py-3">
                          <Loader2 className="h-4 w-4 animate-spin inline-block text-muted-foreground" />
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              ) : (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  {t('drilldown.noRecords')}
                </div>
              )
            ) : drillDownRecords.type === 'recovery' ? (
              drillDownRecords.data.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 sticky top-0 z-10">
                    <tr className="border-b">
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.recoveryId')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.epc')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.client')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.location')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.scanDate')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('drilldown.columns.completeDate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(drillDownRecords.data as any[]).map((r: any, idx: number) => (
                      <tr key={`${r.recoverPalletId}-${idx}`} className="border-b hover:bg-muted/30">
                        <td className="px-3 py-2 font-mono text-xs">{r.recoverPalletId}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.epc}</td>
                        <td className="px-3 py-2">{r.clientName ?? '-'}</td>
                        <td className="px-3 py-2">{r.locationName ?? '-'}</td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {format(new Date(r.scanDate), 'd MMM yyyy', { locale: es })}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {r.completeDate ? format(new Date(r.completeDate), 'd MMM yyyy', { locale: es }) : '-'}
                        </td>
                      </tr>
                    ))}
                    <tr><td colSpan={6}><div ref={sentinelRef} className="h-1" /></td></tr>
                  </tbody>
                  {isFetchingNextPage && (
                    <tfoot>
                      <tr>
                        <td colSpan={6} className="text-center py-3">
                          <Loader2 className="h-4 w-4 animate-spin inline-block text-muted-foreground" />
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              ) : (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  {t('drilldown.noRecords')}
                </div>
              )
            ) : null}
          </div>

          <DialogFooter className="flex-row items-center justify-between sm:justify-between">
            <span className="text-xs text-muted-foreground">
              {drillDownRecords.data.length > 0 && drillDownRecords.totalCount > 0
                ? `${drillDownRecords.data.length} / ${drillDownRecords.totalCount}`
                : ''}
            </span>
            <Button variant="outline" onClick={() => { setDrillDown(null); setDrillDownSearch(''); }}>
              {t('common:buttons.close', 'Cerrar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Phase 6 — local helper that resolves which bound workflow definition's
 * KPIs to render on this tenant's dashboard.
 *
 * For the first commit we show tiles for *every* unique bound workflow
 * definition. A tenant with one binding (the Hospital Pilot case) sees
 * one row; a tenant with several bound AssetClasses sees each row stacked.
 *
 * If a future iteration needs an AssetClass picker (so the admin picks
 * which binding is "primary"), that selection plugs in here without
 * touching WorkflowKpiCardGrid.
 */
function DynamicWorkflowKpis() {
  const { data: bindings } = useWorkflowBindings();
  if (!bindings || bindings.length === 0) return null;
  const uniqueDefinitionIds = Array.from(
    new Set(bindings.map((b) => b.workflowDefinitionId))
  );
  return (
    <div className="space-y-3">
      {uniqueDefinitionIds.map((id) => (
        <WorkflowKpiCardGrid key={id} workflowDefinitionId={id} />
      ))}
      {/* R3 (Wave 4) — compliance % per (state, alert) over the last
          7 days. Pairs with the DwellAlertsCard below: this is the
          "how are we doing overall?" card, that is the "what just
          broke?" card. Both self-collapse on empty data. */}
      <SlaComplianceCard />
      {/* P12 — recent SLA fires. Self-collapsing when empty so tenants
          without dwell alerts see no dead UI. */}
      <DwellAlertsCard />
    </div>
  );
}
